import { create } from 'zustand';

/**
 * The right panel's shape, kept per browser (decided 2026-10-04): how wide it
 * is, whether it is open or folded to its rail of icons, and how the dock
 * under the tabs (the Discussion) stands — open at a share of the panel's
 * height, folded to its header, or taking the whole panel. "max" is for
 * reading a long thread and never outlives the page: the next page opens the
 * dock at its last open share.
 */
export const PANEL_DEFAULT_WIDTH = 300;
/** Three tabs, the label thumb and the fold button still fit at this width. */
export const PANEL_MIN_WIDTH = 260;
/** Never wider than this, nor than half the window (panelMaxWidth). */
export const PANEL_CAP_WIDTH = 640;
export const RAIL_WIDTH = 40;
export const DOCK_DEFAULT_SHARE = 0.34;
export const DOCK_MIN_SHARE = 0.15;
export const DOCK_MAX_SHARE = 0.8;

export type DockState = 'open' | 'collapsed' | 'max';

export function panelMaxWidth(windowWidth: number): number {
  return Math.max(PANEL_MIN_WIDTH, Math.min(PANEL_CAP_WIDTH, Math.floor(windowWidth / 2)));
}

export function clampPanelWidth(width: number, windowWidth: number): number {
  if (!Number.isFinite(width)) return PANEL_DEFAULT_WIDTH;
  return Math.round(Math.min(panelMaxWidth(windowWidth), Math.max(PANEL_MIN_WIDTH, width)));
}

export function clampDockShare(share: number): number {
  if (!Number.isFinite(share)) return DOCK_DEFAULT_SHARE;
  return Math.min(DOCK_MAX_SHARE, Math.max(DOCK_MIN_SHARE, share));
}

export interface PanelPrefs {
  width: number;
  open: boolean;
  dock: Exclude<DockState, 'max'>;
  dockShare: number;
}

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const KEYS = {
  width: 'viewer:panelWidth',
  open: 'viewer:panelOpen',
  dock: 'viewer:dock',
  dockShare: 'viewer:dockShare',
} as const;

export const PANEL_DEFAULTS: PanelPrefs = { width: PANEL_DEFAULT_WIDTH, open: true, dock: 'open', dockShare: DOCK_DEFAULT_SHARE };

/** What this browser remembers, each value checked; anything missing or odd is the default. */
export function readPanelPrefs(storage: StorageLike | null | undefined): PanelPrefs {
  if (!storage) return PANEL_DEFAULTS;
  try {
    const rawWidth = storage.getItem(KEYS.width);
    const width = Number(rawWidth);
    const open = storage.getItem(KEYS.open);
    const dock = storage.getItem(KEYS.dock);
    const rawShare = storage.getItem(KEYS.dockShare);
    const share = Number(rawShare);
    return {
      // The window is not known here: bounded by the fixed limits, and by the window where it is used.
      width: rawWidth === null || !Number.isFinite(width) || width <= 0 ? PANEL_DEFAULTS.width : Math.min(PANEL_CAP_WIDTH, Math.max(PANEL_MIN_WIDTH, Math.round(width))),
      open: open === null ? true : open === '1',
      dock: dock === 'collapsed' ? 'collapsed' : 'open',
      dockShare: rawShare === null || !Number.isFinite(share) ? PANEL_DEFAULTS.dockShare : clampDockShare(share),
    };
  } catch {
    return PANEL_DEFAULTS;
  }
}

function browserStorage(): StorageLike | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function remember(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* private browsing: the choice lasts this page */
  }
}

interface RightPanelStore {
  width: number;
  open: boolean;
  dockState: DockState;
  dockShare: number;
  /** `remember` false while a drag is under way: the browser keeps the value the drag ends on. */
  setWidth: (width: number, remember?: boolean) => void;
  setOpen: (open: boolean) => void;
  setDock: (state: DockState) => void;
  setDockShare: (share: number, remember?: boolean) => void;
}

export const useRightPanel = create<RightPanelStore>((set) => {
  const prefs = readPanelPrefs(browserStorage());
  return {
    width: prefs.width,
    open: prefs.open,
    dockState: prefs.dock,
    dockShare: prefs.dockShare,
    setWidth: (width, keep = true) => {
      if (keep) remember(KEYS.width, String(width));
      set({ width });
    },
    setOpen: (open) => {
      remember(KEYS.open, open ? '1' : '0');
      set({ open });
    },
    setDock: (dockState) => {
      if (dockState !== 'max') remember(KEYS.dock, dockState);
      set({ dockState });
    },
    setDockShare: (share, keep = true) => {
      const dockShare = clampDockShare(share);
      if (keep) remember(KEYS.dockShare, String(dockShare));
      set({ dockShare });
    },
  };
});
